<template>
  <div class="min-h-screen pb-10">
    <!-- En-tête avec bouton de retour -->
    <div class="flex items-center justify-between mb-6">
      <BaseButton variant="ghost" @click="$router.push({ name: 'sales' })">
        <template #icon-left>
          <i class="fa-solid fa-arrow-left"></i>
        </template>
        Retour
      </BaseButton>
      <div class="flex items-center gap-3">
        <BaseButton variant="outline-primary" size="sm" @click="previewInvoice" :loading="isDownloading">
          <i class="fa-solid fa-eye me-2"></i>
          Aperçu
        </BaseButton>
        <BaseButton variant="outline-secondary" size="sm" @click="downloadInvoice" :loading="isDownloading">
          <i class="fa-solid fa-download me-2"></i>
          Télécharger
        </BaseButton>
        <BaseButton variant="success" size="sm" @click="printInvoice">
          <i class="fa-solid fa-print me-2"></i>
          Imprimer
        </BaseButton>
        <BaseButton variant="primary" size="sm" @click="printSaleTicket" :loading="isPrintingTicket"
          :disabled="!sale.id">
          <i class="fa-solid fa-receipt me-2"></i>
          Ticket
        </BaseButton>
      </div>
    </div>

    <!-- Loader -->
    <BaseCard v-if="loading">
      <div class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement...</p>
      </div>
    </BaseCard>

    <!-- Contenu principal -->
    <div v-else-if="sale.id" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Colonne gauche: Infos -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Carte principale -->
        <BaseCard>
          <template #header>
            <div class="flex items-start justify-between w-full">
              <div>
                <h2 class="text-2xl font-bold text-gray-900 mb-1">{{ sale.invoice_number }}</h2>
                <p class="text-sm text-gray-600">{{ formatDate(sale.sale_date) }}</p>
              </div>
              <SaleStatusBadge :sale="sale" />
            </div>
          </template>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Type de vente</span>
              <span class="text-sm font-medium text-gray-900">{{ getSaleTypeLabel(sale.type) }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Client</span>
              <span class="text-sm font-medium text-gray-900">{{ sale.customer?.name || 'Client anonyme' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Vendeur</span>
              <span class="text-sm font-medium text-gray-900">{{ sale.user?.name || 'N/A' }}</span>
            </div>
            <div v-if="sale.payments && sale.payments.length > 0" class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Moyen de paiement</span>
              <span class="text-sm font-medium text-gray-900">{{ getPaymentMethodName(sale.payments[0]) }}</span>
            </div>
          </div>
        </BaseCard>

        <!-- Tableau produits -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Articles ({{ sale.sale_items?.length || 0 }})</h3>
          </template>

          <div class="space-y-2">
            <div v-for="item in sale.sale_items" :key="item.id"
              class="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div
                class="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                <img v-if="item.product.image_url" :src="item.product.image_url" :alt="item.product.name"
                  class="w-full h-full object-cover" />
                <div v-else class="text-gray-400">
                  <i class="fa-solid fa-image text-xl"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                  {{ item.product.name }}
                  <span v-if="item.note"
                    class="relative group inline-flex items-center justify-center w-4 h-4 rounded bg-red-100 cursor-help"
                    :aria-label="`Note: ${item.note}`">
                    <i class="fa-solid fa-exclamation text-red-600 text-xs"></i>
                    <span
                      class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-normal max-w-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      {{ item.note }}
                    </span>
                  </span>
                </p>
                <p class="text-xs text-gray-600">{{ formatCurrency(item.unit_price) }} × {{ item.quantity }}</p>
              </div>
              <div class="text-sm font-semibold text-gray-900">
                {{ formatCurrency(item.total_amount) }}
              </div>
            </div>
          </div>

          <div class="border-t border-gray-200 mt-4 pt-4 space-y-2">
            <div v-if="parseFloat(sale.discount) > 0" class="flex justify-between text-sm">
              <span class="text-gray-600">Remise</span>
              <span class="font-medium text-red-600">-{{ formatCurrency(sale.discount) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t-2 border-gray-200">
              <span class="text-lg font-bold text-primary">Total</span>
              <span class="text-lg font-bold text-primary">{{ formatCurrency(sale.total_incl_tax) }}</span>
            </div>
          </div>
        </BaseCard>
      </div>

      <!-- Colonne droite: Actions -->
      <div class="space-y-6">
        <!-- Facture associée -->
        <BaseCard v-if="sale.sale_invoice" class=" bg-gray-50">
          <template #header>
            <div class="flex items-center justify-between w-full">
              <h3 class="text-lg font-semibold text-gray-900">Facture associée</h3>
              <span :class="[
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                getInvoiceStatusClass(sale.sale_invoice.status)
              ]">
                {{ getInvoiceStatusLabel(sale.sale_invoice.status) }}
              </span>
            </div>
          </template>

          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-dark-700">N° Facture</span>
              <span class="text-sm font-semibold text-dark-900 font-mono">{{ sale.sale_invoice.invoice_number }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-dark-700">Montant total</span>
              <span class="text-sm font-medium text-dark-900">{{ formatCurrency(sale.sale_invoice.total_amount) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-dark-700">Montant payé</span>
              <span class="text-sm font-medium text-green-600">{{ formatCurrency(sale.sale_invoice.paid_amount) }}</span>
            </div>
            <div v-if="sale.sale_invoice.total_amount - sale.sale_invoice.paid_amount > 0" class="flex justify-between items-center">
              <span class="text-sm text-dark-700">Reste à payer</span>
              <span class="text-sm font-medium text-red-600">{{ formatCurrency(sale.sale_invoice.total_amount - sale.sale_invoice.paid_amount) }}</span>
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-dark-200">
            <BaseButton variant="outline-primary" block size="sm" @click="viewSaleInvoice">
              <template #icon-left>
                <i class="fa-solid fa-file-invoice"></i>
              </template>
              Voir la facture
            </BaseButton>
          </div>
        </BaseCard>

        <!-- Résumé paiement et actions -->
        <BaseCard v-if="!sale.sale_invoice" :class="getPaymentCardClass">
          <template #header>
            <div class="flex items-center justify-between w-full">
              <h3 class="text-lg font-semibold" :class="getPaymentTitleClass">{{ getPaymentTitle }}</h3>
              <span v-if="sale.payments && sale.payments.length > 0" class="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {{ sale.payments.length }} paiement(s)
              </span>
            </div>
          </template>

          <!-- Résumé montants -->
          <div class="space-y-2 mb-4">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Total vente</span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Payé</span>
              <span class="text-sm font-semibold text-green-600">{{ formatCurrency(paidAmount) }}</span>
            </div>
            <div v-if="remainingAmount > 0" class="flex justify-between items-center pt-2 border-t border-gray-200">
              <span class="text-sm font-medium text-gray-700">Reste à payer</span>
              <span class="text-sm font-bold text-red-600">{{ formatCurrency(remainingAmount) }}</span>
            </div>
          </div>

          <!-- Liste des paiements compacte -->
          <div v-if="sale.payments && sale.payments.length > 0" class="mb-4">
            <div class="text-xs font-medium text-gray-500 uppercase mb-2">Historique</div>
            <div class="space-y-1.5 max-h-32 overflow-y-auto">
              <div v-for="payment in sale.payments" :key="payment.id"
                class="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-900">{{ formatCurrency(payment.amount) }}</span>
                  <span class="text-gray-500">{{ getPaymentMethodName(payment) }}</span>
                </div>
                <span class="text-gray-400">{{ formatShortDate(payment.payment_date) }}</span>
              </div>
            </div>
          </div>

          <!-- Bouton d'encaissement si reste à payer -->
          <BaseButton v-if="canMakePayment" variant="primary" block @click="openPaymentModal">
            <template #icon-left>
              <i class="fa-solid fa-credit-card"></i>
            </template>
            {{ remainingAmount > 0 && paidAmount > 0 ? 'Compléter le paiement' : 'Encaisser la vente' }}
          </BaseButton>

          <!-- Bouton télécharger reçu si payé -->
          <BaseButton v-if="sale.status === 'paid' && sale.payments && sale.payments.length > 0" variant="ghost" block @click="downloadPaymentReceipt" :loading="isDownloadingReceipt">
            <template #icon-left>
              <i class="fa-solid fa-download"></i>
            </template>
            Télécharger le reçu
          </BaseButton>
        </BaseCard>

        <!-- Actions rapides -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Actions</h3>
          </template>

          <div class="space-y-2">
            <BaseButton v-if="sale.status != 'refunded'" variant="danger" block @click="showRefundModal = true">
              <template #icon-left>
                <i class="fa-solid fa-undo"></i>
              </template>
              Rembourser
            </BaseButton>
            <BaseButton variant="ghost" block @click="duplicateSale">
              <template #icon-left>
                <i class="fa-solid fa-copy"></i>
              </template>
              Dupliquer
            </BaseButton>
            <BaseButton variant="ghost" block @click="sendBySMS">
              <template #icon-left>
                <i class="fa-solid fa-sms"></i>
              </template>
              Envoyer par SMS
            </BaseButton>
            <BaseButton variant="ghost" block @click="sendByEmail">
              <template #icon-left>
                <i class="fa-solid fa-envelope"></i>
              </template>
              Envoyer par Email
            </BaseButton>
            <BaseButton variant="ghost" block @click="sendByWhatsApp">
              <template #icon-left>
                <i class="fa-brands fa-whatsapp"></i>
              </template>
              WhatsApp
            </BaseButton>
          </div>
        </BaseCard>

        <!-- Remboursements -->
        <BaseCard v-if="sale.refunds && sale.refunds.length > 0">
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Remboursements</h3>
          </template>

          <div class="space-y-2">
            <div v-for="refund in sale.refunds" :key="refund.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold text-red-600">{{ formatCurrency(refund.amount) }}</span>
                <span class="text-xs text-gray-600">{{ formatDate(refund.refund_date) }}</span>
              </div>
              <span class="text-xs text-gray-600">
                {{ getPaymentMethodName({ payment_type: refund.payment_type }) }}
              </span>
            </div>
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Message d'erreur -->
    <BaseCard v-else-if="!loading && !sale.id">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-gray-300 mb-4">
          <i class="fa-solid fa-exclamation-circle text-6xl"></i>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Vente introuvable</h2>
        <p class="text-gray-600 mb-6">La vente que vous recherchez n'existe pas.</p>
        <BaseButton variant="primary" @click="$router.push({ name: 'sales' })">
          Retour aux ventes
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Modal de remboursement -->
    <RefundModal :show="showRefundModal" :sale="sale" :cash-registers="cashRegisters" @close="showRefundModal = false"
      @refunded="onRefundSuccess" />

    <!-- Modal de paiement -->
    <PaymentModal :show="showPaymentModal" :sale="sale" :cash-registers="cashRegisters"
      @close="showPaymentModal = false" @payment-success="onPaymentSuccess" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { salesService } from '@/services'
import CashRegisterService from '@/services/cashRegisterService'
import paymentService from '@/services/paymentService'
import thermalPrinterService from '@/services/thermalPrinterService'
import RefundModal from './RefundModal.vue'
import PaymentModal from './PaymentModal.vue'
import { useToastStore } from '@/store/modules/toastStore'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'

export default defineComponent({
  name: 'SaleDetailsView',
  components: {
    RefundModal,
    PaymentModal,
    BaseCard,
    BaseButton,
    SaleStatusBadge
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const sale = ref({})
    const loading = ref(true)
    const showRefundModal = ref(false)
    const showPaymentModal = ref(false)
    const isDownloading = ref(false)
    const isDownloadingReceipt = ref(false)
    const isPrintingTicket = ref(false)
    const cashRegisters = ref([])

    // Computed properties for payment summary
    const paidAmount = computed(() => {
      if (!sale.value.payments || sale.value.payments.length === 0) return 0
      return sale.value.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
    })

    const remainingAmount = computed(() => {
      return (sale.value.total_incl_tax || 0) - paidAmount.value
    })

    const canMakePayment = computed(() => {
      // Can make payment if status is pending or partial_paid
      const allowedStatuses = ['pending', 'partial_paid']
      return allowedStatuses.includes(sale.value.status) && !sale.value.sale_invoice
    })

    const getPaymentCardClass = computed(() => {
      if (sale.value.status === 'paid') {
        return 'border-2 border-green-200 bg-green-50'
      }
      if (sale.value.status === 'partial_paid') {
        return 'border-2 border-blue-200 bg-blue-50'
      }
      if (sale.value.status === 'pending' || remainingAmount.value > 0) {
        return 'border-2 border-yellow-200 bg-yellow-50'
      }
      return ''
    })

    const getPaymentTitleClass = computed(() => {
      if (sale.value.status === 'paid') return 'text-green-900'
      if (sale.value.status === 'partial_paid') return 'text-blue-900'
      return 'text-yellow-900'
    })

    const getPaymentTitle = computed(() => {
      if (sale.value.status === 'paid') return '✓ Vente payée'
      if (sale.value.status === 'partial_paid') return 'Paiement partiel'
      return 'En attente de paiement'
    })

    const loadSaleDetails = async () => {
      const saleId = route.params.id
      if (!saleId) {
        router.push({ name: 'sales' })
        return
      }

      try {
        loading.value = true
        const response = await salesService.getSale(saleId, {
          with: ['user', 'saleItems', 'customer', 'saleItems.product', 'payments', 'saleInvoice']
        })
        console.log(response.data)
        sale.value = response.data.data
      } catch (err) {
        console.error('Erreur:', err)
        sale.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadCashRegisters = async () => {
      try {
        const response = await CashRegisterService.getCashRegisters({
          status: 'open',
          per_page: 100
        })
        cashRegisters.value = response.data || []
      } catch (err) {
        console.error('Erreur chargement caisses:', err)
        cashRegisters.value = []
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatShortDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      })
    }

    const getStatusLabel = (status) => {
      const labels = {
        'pending': 'En attente',
        'completed': 'Complétée',
        'refunded': 'Remboursée',
        'partial_refund': 'Remb. partiel'
      }
      return labels[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const statusClasses = {
        'completed': 'bg-green-100 text-green-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'refunded': 'bg-red-100 text-red-800',
        'partial_refund': 'bg-orange-100 text-orange-800'
      }
      return statusClasses[status] || 'bg-gray-100 text-gray-800'
    }

    const getSaleTypeLabel = (type) => {
      return type === 'deferred' ? 'Vente différée' : 'Vente comptant'
    }

    const getInvoiceStatusLabel = (status) => {
      const labels = {
        'pending': 'En attente',
        'partial': 'Partielle',
        'paid': 'Payée'
      }
      return labels[status] || status
    }

    const getInvoiceStatusClass = (status) => {
      const classes = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'partial': 'bg-blue-100 text-blue-800',
        'paid': 'bg-green-100 text-green-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    const viewSaleInvoice = () => {
      if (sale.value.sale_invoice?.id) {
        router.push({ name: 'sale-invoice-details', params: { id: sale.value.sale_invoice.id } })
      }
    }

    const getPaymentMethodName = (payment) => {
      if (!payment) return 'N/A'
      const types = {
        'cash': 'Espèces',
        'card': 'Carte',
        'mobile_money': 'Mobile Money',
        'orange_money': 'Orange Money',
        'wave': 'Wave',
        'check': 'Chèque',
        'bank_transfer': 'Virement',
        'other': 'Autre'
      }
      return types[payment.payment_type] || payment.payment_type
    }

    const downloadInvoice = async () => {
      if(isDownloading.value) return
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.value.id, false)
      } catch (err) {
        console.error('Erreur téléchargement:', err)
      } finally {
        isDownloading.value = false
      }
    }

    const previewInvoice = async () => {
      if(isDownloading.value) return
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.value.id, true)
      } catch (err) {
        console.error('Erreur aperçu:', err)
      } finally {
        isDownloading.value = false
      }
    }

    const printInvoice = () => {
      console.log('TODO: Imprimer la facture', sale.value.id)
    }

    const printSaleTicket = async () => {
      if(isPrintingTicket.value) return
      if (!sale.value || !sale.value.id) return
      try {
        isPrintingTicket.value = true
        console.clear()
        console.log(sale.value)
        await thermalPrinterService.printTicket(sale.value, {
          connectionType: 'usb',
          shopInfo: JSON.parse(localStorage.getItem('company'))
        })
        toastStore.addToast({
          type: 'success',
          message: 'Ticket envoyé à l\'imprimante',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur impression ticket:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Impossible d\'imprimer le ticket',
          duration: 4000
        })
      } finally {
        isPrintingTicket.value = false
      }
    }

    const openPaymentModal = () => {
      showPaymentModal.value = true
    }

    const onPaymentSuccess = async () => {
      showPaymentModal.value = false
      await loadSaleDetails()
      toastStore.addToast({
        type: 'success',
        message: 'Paiement enregistré avec succès',
        duration: 4000
      })
    }

    const duplicateSale = () => {
      console.log('TODO: Dupliquer la vente', sale.value.id)
    }

    const sendBySMS = () => {
      console.log('TODO: Envoyer par SMS', sale.value.id)
    }

    const sendByEmail = () => {
      console.log('TODO: Envoyer par Email', sale.value.id)
    }

    const sendByWhatsApp = () => {
      console.log('TODO: Envoyer par WhatsApp', sale.value.id)
    }

    const onRefundSuccess = async () => {
      showRefundModal.value = false
      await loadSaleDetails()
      toastStore.addToast({
        type: 'success',
        message: 'Remboursement effectué avec succès',
        duration: 4000
      })
    }

    const downloadPaymentReceipt = async () => {
      if (!sale.value.payments || sale.value.payments.length === 0) {
        toastStore.addToast({
          type: 'error',
          message: 'Aucun paiement trouvé pour cette vente',
          duration: 4000
        })
        return
      }

      try {
        isDownloadingReceipt.value = true
        // Télécharger le reçu du dernier paiement
        const lastPayment = sale.value.payments[sale.value.payments.length - 1]
        await paymentService.printPaymentReceipt(lastPayment.id, false)
        toastStore.addToast({
          type: 'success',
          message: 'Reçu de paiement téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur téléchargement reçu:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement du reçu',
          duration: 4000
        })
      } finally {
        isDownloadingReceipt.value = false
      }
    }

    onMounted(() => {
      loadSaleDetails()
      loadCashRegisters()
    })

    return {
      sale,
      loading,
      showRefundModal,
      showPaymentModal,
      isDownloading,
      isDownloadingReceipt,
      isPrintingTicket,
      cashRegisters,
      // Computed
      paidAmount,
      remainingAmount,
      canMakePayment,
      getPaymentCardClass,
      getPaymentTitleClass,
      getPaymentTitle,
      // Methods
      formatCurrency,
      formatDate,
      formatShortDate,
      getStatusLabel,
      getStatusBadgeClass,
      getSaleTypeLabel,
      getInvoiceStatusLabel,
      getInvoiceStatusClass,
      viewSaleInvoice,
      getPaymentMethodName,
      downloadInvoice,
      previewInvoice,
      printInvoice,
      printSaleTicket,
      openPaymentModal,
      onPaymentSuccess,
      downloadPaymentReceipt,
      duplicateSale,
      sendBySMS,
      sendByEmail,
      sendByWhatsApp,
      onRefundSuccess
    }
  }
})
</script>

<style scoped>
/* Styles spécifiques si nécessaire - la plupart est gérée par Tailwind */
</style>
